/**
 * Decodes an invite token to extract email and expiry.
 *
 * Token format: `base64url(email|expiry).signature`
 *
 * Note: This function does NOT verify the HMAC signature. The token is only
 * used to mask email addresses in URLs (low-security use case), so verification
 * is not required.
 *
 * @param token - The invite token from query parameter
 * @returns Decoded email and expiry timestamp
 * @throws Error if token is invalid, malformed, or expired
 *
 * @example
 * ```typescript
 * const token = 'bHVAc2l4LWludmVzdC5jb20uYXV8MTcyMzA1NjAwMDAwMA.abc123'
 * const { email, expiry } = decodeInviteToken(token)
 * // email: 'lu@six-invest.com.au'
 * // expiry: 1723056000000
 * ```
 */
export function decodeInviteToken(token: string): { email: string; expiry: number } {
    if (!token) {
        throw new Error('Token is required')
    }

    // Token format: base64url(email|expiry).signature
    const parts = token.split('.')
    if (parts.length !== 2) {
        throw new Error('Invalid token format')
    }

    const [payloadB64] = parts

    // Decode base64url payload
    let payload: string
    try {
        // Convert base64url to standard base64
        let base64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
        // Add padding if needed (base64 strings must have length % 4 === 0)
        const padding = (4 - (base64.length % 4)) % 4
        base64 += '='.repeat(padding)
        // Decode using atob (browser) or Buffer (Node.js for tests)
        if (typeof atob !== 'undefined') {
            payload = atob(base64)
        } else if (typeof Buffer !== 'undefined') {
            payload = Buffer.from(base64, 'base64').toString('utf-8')
        } else {
            throw new Error('No base64 decoder available')
        }
    } catch {
        throw new Error('Invalid base64 encoding in token')
    }

    // Parse payload: email|expiry
    const payloadParts = payload.split('|')
    if (payloadParts.length !== 2) {
        throw new Error('Invalid token payload format')
    }

    const [email, expiryStr] = payloadParts

    if (!email || !expiryStr) {
        throw new Error('Invalid token payload: missing email or expiry')
    }

    const expiry = parseInt(expiryStr, 10)
    if (isNaN(expiry)) {
        throw new Error('Invalid expiry timestamp in token')
    }

    // Check if token is expired
    if (Date.now() > expiry) {
        throw new Error('Token has expired')
    }

    return { email, expiry }
}
